<template>
	<div
		class="rich-text-editor p-0 has-background-white has-text-left"
		:class="{ 'is-max-height': isMaxHeight }">
		<div
			v-if="editor"
			class="columns is-vcentered has-background-white is-multiline is-variable is-0 grey-border-bottom mt-1 ml-1 mr-1 mb-0">
			<div class="column is-narrow p-0 m-0">
				<button
					@click="bold"
					data-tippy-content="Bold"
					class="button p-2"
					:class="{
						'is-info': editor.isActive('bold'),
						'is-light': editor.isActive('bold'),
						'is-white': !editor.isActive('bold'),
					}">
					<i class="fas fa-bold"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="italic"
					data-tippy-content="Italic"
					class="button is-white p-2"
					:class="{
						'is-info': editor.isActive('italic'),
						'is-light': editor.isActive('italic'),
						'is-white': !editor.isActive('italic'),
					}">
					<i class="fas fa-italic"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="underline"
					data-tippy-content="Underline"
					class="button is-white p-2"
					:class="{
						'is-info': editor.isActive('underline'),
						'is-light': editor.isActive('underline'),
						'is-white': !editor.isActive('underline'),
					}">
					<i class="fas fa-underline"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="strikethrough"
					data-tippy-content="Strikethrough"
					class="button is-white p-2"
					:class="{
						'is-info': editor.isActive('strike'),
						'is-light': editor.isActive('strike'),
						'is-white': !editor.isActive('strike'),
					}">
					<i class="fas fa-strikethrough"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="clearFormatting"
					data-tippy-content="Remove Text Markup"
					class="button is-white p-2">
					<i class="fas fa-remove-format"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 mt-0 mb-0 ml-2 mr-2">
				<button
					@click="editor.chain().focus().clearNodes().run()"
					data-tippy-content="Clear Formatting"
					class="button is-white p-2">
					<span style="transform: rotate(220deg)"
						><i class="fas fa-brush"></i
					></span>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="setParagraph"
					data-tippy-content="Paragraph"
					class="button is-white p-2"
					:class="{
						'is-info': editor.isActive('paragraph'),
						'is-light': editor.isActive('paragraph'),
						'is-white': !editor.isActive('paragraph'),
					}">
					<i class="fas fa-paragraph"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="toggleHeading4"
					data-tippy-content="Heading"
					class="button is-white p-2"
					:class="{
						'is-info': editor.isActive('heading', { level: 4 }),
						'is-light': editor.isActive('heading', { level: 4 }),
						'is-white': !editor.isActive('heading', { level: 4 }),
					}">
					<i class="fas fa-heading"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="toggleBulletList"
					data-tippy-content="Bullet List"
					class="button is-white p-2"
					:class="{
						'is-info': editor.isActive('bulletList'),
						'is-light': editor.isActive('bulletList'),
						'is-white': !editor.isActive('bulletList'),
					}">
					<i class="fas fa-list-ul"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="toggleOrderedList"
					data-tippy-content="Ordered List"
					class="button is-white p-2"
					:class="{
						'is-info': editor.isActive('orderedList'),
						'is-light': editor.isActive('orderedList'),
						'is-active': editor.isActive('orderedList'),
					}">
					<i class="fas fa-list-ol"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 mt-0 mb-0 ml-2 mr-2">
				<button
					@click="setHorizontalRule"
					data-tippy-content="Horizontal Rule"
					class="button is-white p-2">
					<b>—</b>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="undo"
					class="button is-white p-2"
					data-tippy-content="Undo">
					<i class="fas fa-undo-alt"></i>
				</button>
			</div>
			<div class="column is-narrow p-0 m-0">
				<button
					@click="redo"
					class="button is-white p-2"
					data-tippy-content="Redo">
					<i class="fas fa-redo-alt"></i>
				</button>
			</div>
			<div class="column p-0 m-0 has-text-right pr-2">
				<label class="label is-inline"
					><span class="has-text-weight-normal is-size-7"
						>{{ characterLength }} / {{ maxCharacterLength }}</span
					></label
				>
			</div>
		</div>
		<div
			ref="content_holder"
			@click="focus($event)"
			class="has-background-white m-1 pl-2 pr-2 is-clickable"
			:class="{ 'is-vertially-resizable': canResizeVertically }"
			style="min-height: 10rem; overflow-y: auto">
			<editor-content
				style="overflow-y: auto"
				:editor="editor" />
		</div>
	</div>
</template>
<style>
	.is-clickable {
		cursor: pointer;
	}

	.rich-text-editor {
		border: solid 1px #dcdcdc;
		font-family: "serif";
		border-radius: 7px;
	}

	.ProseMirror > grey-border-bottom {
		border: solid 1px #dcdcdc;
	}

	.ProseMirror > ul {
		padding: 0 1em;
		list-style-type: disc;
	}

	.ProseMirror > ol {
		padding: 0 1.25em;
	}

	.ProseMirror > h4 {
		font-size: 1.5rem;
		font-weight: bold;
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
	}

	.ProseMirror > hr {
		margin-top: 1em;
		margin-bottom: 1em;
		background-color: #6a6a6a;
	}

	.ProseMirror p.is-editor-empty:first-child::before {
		content: attr(data-placeholder);
		float: left;
		color: #adb5bd;
		pointer-events: none;
		height: 0;
	}

	.is-vertically-resizable {
		resize: vertical;
	}

	.is-max-height {
		height: 100%;
	}
</style>
<script>
	import { Editor, EditorContent } from "@tiptap/vue-2";
	import StarterKit from "@tiptap/starter-kit";
	import Placeholder from "@tiptap/extension-placeholder";
	import OrderedList from "@tiptap/extension-ordered-list";
	import BulletList from "@tiptap/extension-bullet-list";
	import ListItem from "@tiptap/extension-list-item";
	import Heading from "@tiptap/extension-heading";
	import Underline from "@tiptap/extension-underline";
	import CharacterCount from "@tiptap/extension-character-count";

	import tippy from "tippy.js";
	import "tippy.js/dist/tippy.css";

	export default {
		props: {
			placeholder_prop: String,
			maxCharacterLength_prop: Number,
			canResizeVertically_prop: Boolean,
			isMaxHeight_prop: Boolean,
		},
		components: {
			EditorContent,
		},
		data() {
			return {
				editor: null,
			};
		},
		computed: {
			placeholder() {
				return this.placeholder_prop
					? this.placeholder_prop
					: "Start Typing Here...";
			},
			canResizeVertically() {
				return this.canResizeVertically_prop
					? this.canResizeVertically_prop
					: false;
			},
			isMaxHeight() {
				return this.isMaxHeight_prop ? this.isMaxHeight_prop : false;
			},
			maxCharacterLength() {
				return this.maxCharacterLength_prop
					? this.maxCharacterLength_prop
					: 400;
			},
			characterLength() {
				return this.editor.storage.characterCount.characters();
			},
		},
		mounted() {
			let _this = this;
			this.editor = new Editor({
				extensions: [
					StarterKit,
					Placeholder.configure({
						placeholder: _this.placeholder,
					}),
					CharacterCount.configure({
						limit: _this.maxCharacterLength,
					}),
					OrderedList,
					ListItem,
					Underline,
					Heading,
				],
			});
			setTimeout(() => {
				tippy("[data-tippy-content]"),
					{
						delay: [200, 400],
						duration: [600, 450],
					};
			}, 500);
		},
		beforeDestroy() {
			this.editor.destroy();
		},
		methods: {
			bold() {
				this.editor.chain().focus().toggleBold().run();
			},
			italic() {
				this.editor.chain().focus().toggleItalic().run();
			},
			underline() {
				this.editor.chain().focus().toggleUnderline().run();
			},
			strikethrough() {
				this.editor.chain().focus().toggleStrike().run();
			},
			clearFormatting() {
				this.editor.chain().focus().unsetAllMarks().run();
			},
			setParagraph() {
				this.editor.chain().focus().setParagraph().run();
			},
			setHorizontalRule() {
				this.editor.chain().focus().setHorizontalRule().run();
			},
			toggleBulletList() {
				this.editor.chain().focus().toggleBulletList().run();
			},
			toggleOrderedList() {
				this.editor.chain().focus().toggleOrderedList().run();
			},
			toggleHeading1() {
				this.editor.chain().focus().toggleHeading({ level: 1 }).run();
			},
			toggleHeading2() {
				this.editor.chain().focus().toggleHeading({ level: 2 }).run();
			},
			toggleHeading3() {
				this.editor.chain().focus().toggleHeading({ level: 3 }).run();
			},
			toggleHeading4() {
				this.editor.chain().focus().toggleHeading({ level: 4 }).run();
			},
			toggleHeading5() {
				this.editor.chain().focus().toggleHeading({ level: 5 }).run();
			},
			toggleHeading6() {
				this.editor.chain().focus().toggleHeading({ level: 6 }).run();
			},
			undo() {
				this.editor.chain().focus().undo().run();
			},
			redo() {
				this.editor.chain().focus().redo().run();
			},
			focus(e) {
				/*
                    Without this 'if' statement anywhere you click inside the
                    component text area will result in the cursor moving to
                    the end of the content. This 'if' statement makes sure
                    that the target is either the text area or it's surrounding
                    parent div, else the cursor will move to where the user
                    clicks as expected.
                */
				if (
					e.target === this.$refs.content_holder ||
					e.target.parentElement === this.$refs.content_holder
				) {
					this.editor.commands.focus("end");
				}
			},
			getHTML() {
				return this.editor.getHTML();
			},
			setHTML(html) {
				this.editor.commands.setContent(html);
			},
			clear() {
				this.editor.commands.setContent("");
			},
		},
	};
</script>
